/* ------------------------------------------------------------------
 * Magni Modular Viewer — Apple "Get the highlights" interaction.
 * Default: main image. Tap a pill -> open feature mode (main crossfades out,
 * feature fades in). Swipe / arrows move between features. ✕ closes back.
 *
 * Editor: remembers the open module across re-renders (so tweaking a module's
 * sliders doesn't snap back to the main view) and opens to the block you select.
 * ------------------------------------------------------------------ */
(function () {
  'use strict';

  // Persists per section id across shopify:section:load DOM rebuilds.
  // Lives on window (not a plain var) because Shopify's theme editor re-executes
  // this in-section <script> on every section reload — a plain var would reset
  // to {} each time, dropping the open module back to the closed/main view.
  var stateBySection = window.MagniModularState || (window.MagniModularState = {});

  function initModular(section) {
    var track = section.querySelector('[data-mod-track]');
    var slides = [].slice.call(section.querySelectorAll('[data-mod-slide]'));
    var pills = [].slice.call(section.querySelectorAll('[data-mod-pill]'));
    var prev = section.querySelector('[data-mod-prev]');
    var next = section.querySelector('[data-mod-next]');
    var dprev = section.querySelector('[data-mod-dprev]'); // desktop up chevron
    var dnext = section.querySelector('[data-mod-dnext]'); // desktop down chevron
    var closeBtn = section.querySelector('[data-mod-close]');
    var captionEl = section.querySelector('[data-mod-caption]');
    if (!track || slides.length < 1) return;

    var sid = section.id;
    var OPEN = 'is-feature-open';
    // Desktop = vertical rail (pill replaced by its description card, image swaps,
    // up/down chevrons step features). Mobile = horizontal track + overlay caption.
    var isDesktop = function () { return window.matchMedia('(min-width: 769px)').matches; };

    // --- Interactive 3D model ---
    // The model is the resting main view (drag to spin, zoom locked). Opening a
    // module fades the model out and shows that module's imagery (the standard
    // is-feature-open behavior); closing fades the model back in.
    var mv = section.querySelector('[data-mod-model]');

    // Fade the model in once loaded (no poster, so no flash). Fallback timeout
    // so a slow/failed load never leaves the stage stuck blank.
    if (mv) {
      var markReady = function () { section.classList.add('is-model-ready'); };
      if (mv.loaded) markReady();
      else { mv.addEventListener('load', markReady); window.setTimeout(markReady, 6000); }
    }

    // Scroll-in entrance (eyebrow → title → stage → pills) now runs through the
    // shared magni-reveal util — those elements carry [data-magni-reveal], so
    // each floats in as it crosses the trigger line instead of all at once when
    // the section first peeks in. In the editor the util reveals immediately.
    if (window.Shopify && window.Shopify.designMode) {
      // Keep the model visible right away so the customizer never shows a blank
      // stage (front-end gets is-model-ready from the model-viewer 'load' above).
      section.classList.add('is-model-ready');
    }

    // Tuning aid: live camera-orbit readout. Drag the model to the resting angle
    // you want, read the numbers off the corner, paste the first two into
    // "Resting camera angle" in the editor. Toggle off before launch.
    if (mv && section.getAttribute('data-mod-orbit-readout') === '1' && !section.querySelector('.magni-modular__orbit-readout')) {
      var readout = document.createElement('div');
      readout.className = 'magni-modular__orbit-readout';
      readout.textContent = 'drag the model…';
      (section.querySelector('.magni-modular__stage') || section).appendChild(readout);
      var fmtOrbit = function () {
        if (typeof mv.getCameraOrbit !== 'function') return;
        var o = mv.getCameraOrbit();
        var deg = function (rad) { return Math.round(rad * 180 / Math.PI); };
        readout.textContent = deg(o.theta) + 'deg ' + deg(o.phi) + 'deg ' + o.radius.toFixed(2) + 'm';
      };
      mv.addEventListener('camera-change', fmtOrbit);
      mv.addEventListener('load', fmtOrbit);
    }

    // Caption text per module (filled into the single caption card on open/swipe).
    var captions = slides.map(function (s) {
      return { title: s.getAttribute('data-title') || '', desc: s.getAttribute('data-desc') || '' };
    });
    var shownCaption = -1;
    function setCaption(i) {
      if (!captionEl || i === shownCaption) return;
      shownCaption = i;
      var c = captions[i] || { title: '', desc: '' };
      captionEl.textContent = '';
      if (c.title) {
        var b = document.createElement('span');
        b.className = 'magni-modular__caption-title';
        b.textContent = c.title;
        captionEl.appendChild(b);
        captionEl.appendChild(document.createTextNode(' '));
      }
      captionEl.appendChild(document.createTextNode(c.desc));
    }

    var current = 0; // logical active slide index (source of truth on desktop)
    var step = function () {
      return slides.length > 1 ? (slides[1].offsetLeft - slides[0].offsetLeft) : track.clientWidth;
    };
    // Mobile reads the index from scroll position; desktop slides are absolutely
    // stacked (no scroll), so `current` is the source of truth there.
    var activeIndex = function () {
      return Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / step())));
    };
    var currentIndex = function () { return isDesktop() ? current : activeIndex(); };
    var isOpen = function () { return section.classList.contains(OPEN); };

    // Show feature i. Desktop: crossfade via .is-shown (CSS eases it in from the
    // right). Mobile: position the horizontal scroll track. Either way `current`
    // tracks it so a resize across the breakpoint doesn't desync the index.
    function showSlide(i) {
      current = i;
      if (isDesktop()) {
        slides.forEach(function (s, k) { s.classList.toggle('is-shown', k === i); });
      } else {
        track.scrollLeft = i * step();
      }
    }

    function syncPills(idx) {
      pills.forEach(function (p, i) { p.classList.toggle('is-active', isOpen() && i === idx); });
      var atStart = idx <= 0;
      var atEnd = idx >= slides.length - 1;
      [prev, dprev].forEach(function (b) { if (b) b.disabled = atStart; });
      [next, dnext].forEach(function (b) { if (b) b.disabled = atEnd; });
    }

    // Step to the previous/next feature. Desktop swaps the image + card directly;
    // mobile smooth-scrolls the horizontal track (its swipe feel).
    function go(delta) {
      if (isDesktop()) {
        var i = Math.max(0, Math.min(slides.length - 1, currentIndex() + delta));
        open(i);
      } else {
        track.scrollBy({ left: delta * step(), behavior: 'smooth' });
      }
    }

    // --- Shared-element morph: caption rises out of the clicked pill, the
    // arrows rise out of the neighbouring pills (and reverse on close). ---
    var lastPillRects = null;
    function neighbors(i) {
      return {
        cap: lastPillRects[i],
        prev: lastPillRects[Math.max(0, i - 1)],
        next: lastPillRects[Math.min(slides.length - 1, i + 1)]
      };
    }
    // Place `el` at `fromRect`'s position (no scale → text doesn't stretch),
    // then release so the CSS transition animates it home.
    function morphFrom(el, fromRect, vertOnly) {
      if (!el || !fromRect) return;
      var to = el.getBoundingClientRect();
      if (!to.width) return;
      var dx = vertOnly ? 0 : (fromRect.left + fromRect.width / 2) - (to.left + to.width / 2);
      var dy = (fromRect.top + fromRect.height / 2) - (to.top + to.height / 2);
      el.style.transition = 'none';
      el.style.transform = 'translate(' + dx + 'px,' + dy + 'px)';
      el.style.opacity = '0';
      el.getBoundingClientRect(); // reflow
      el.style.transition = '';
      el.style.transform = '';
      el.style.opacity = '';
    }
    function morphTo(el, toRect, vertOnly) {
      if (!el || !toRect) return;
      var from = el.getBoundingClientRect();
      if (!from.width) return;
      var dx = vertOnly ? 0 : (toRect.left + toRect.width / 2) - (from.left + from.width / 2);
      var dy = (toRect.top + toRect.height / 2) - (from.top + from.height / 2);
      el.style.transform = 'translate(' + dx + 'px,' + dy + 'px)';
      el.style.opacity = '0';
    }
    function clearMorph() {
      [captionEl, prev, next].forEach(function (el) {
        if (el) { el.style.transform = ''; el.style.opacity = ''; el.style.transition = ''; }
      });
    }

    function open(i) {
      // Capture pill positions while they're still visible (before OPEN hides them).
      lastPillRects = pills.map(function (p) { return p.getBoundingClientRect(); });
      showSlide(i);
      setCaption(i);
      section.classList.add(OPEN);
      syncPills(i);
      stateBySection[sid] = { open: true, index: i };
      // Shared-element morph is the mobile caption/arrow motion only — on desktop
      // the caption/edge-arrows are hidden and the rail card animates via CSS.
      if (!section.classList.contains('no-anim') && lastPillRects.length && !isDesktop()) {
        var n = neighbors(i);
        morphFrom(captionEl, n.cap, true); // caption rises straight up (no sideways drag)
        morphFrom(prev, n.prev);
        morphFrom(next, n.next);
      }
    }

    function close() {
      pills.forEach(function (p) { p.classList.remove('is-active'); });
      // Hide the feature slide so it fades out as the (transparent-bg) main image
      // fades back in — otherwise the last shown slide stays visible and stacks on
      // top of the main shot. No-op on mobile (slides there don't use .is-shown).
      slides.forEach(function (s) { s.classList.remove('is-shown'); });
      var idx = currentIndex();
      stateBySection[sid] = { open: false, index: idx };
      if (lastPillRects && !section.classList.contains('no-anim') && !isDesktop()) {
        var n = neighbors(idx);
        morphTo(captionEl, n.cap, true);
        morphTo(prev, n.prev);
        morphTo(next, n.next);
        window.setTimeout(function () { section.classList.remove(OPEN); clearMorph(); }, 480);
      } else {
        section.classList.remove(OPEN);
        clearMorph();
      }
    }

    // Let the design-mode block-select handler drive this viewer.
    section._magniModularOpen = open;

    pills.forEach(function (pill) {
      pill.addEventListener('click', function () {
        open(parseInt(pill.getAttribute('data-mod-pill'), 10) || 0);
      });
    });
    if (prev) prev.addEventListener('click', function () { go(-1); });
    if (next) next.addEventListener('click', function () { go(1); });
    if (dprev) dprev.addEventListener('click', function () { go(-1); });
    if (dnext) dnext.addEventListener('click', function () { go(1); });
    if (closeBtn) closeBtn.addEventListener('click', close);

    var ticking = false;
    track.addEventListener('scroll', function () {
      if (isDesktop()) return; // desktop has no scroll track (slides crossfade)
      if (!ticking) {
        window.requestAnimationFrame(function () {
          if (isOpen()) {
            var idx = activeIndex();
            current = idx;
            syncPills(idx);
            setCaption(idx);
            stateBySection[sid] = { open: true, index: idx };
          }
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });

    syncPills(currentIndex());

    // Restore the open module after a customizer re-render — without replaying
    // the fade (no-anim), so dragging a slider doesn't flicker the whole stage.
    var st = stateBySection[sid];
    if (st && st.open) {
      section.classList.add('no-anim');
      open(st.index);
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () { section.classList.remove('no-anim'); });
      });
    }
  }

  function init() {
    [].slice.call(document.querySelectorAll('[data-magni-modular]')).forEach(initModular);
  }

  // Bind the one-time init + global listeners only once. If the editor re-runs
  // this script on a section reload, we bail here — the persistent section:load
  // listener (from the first run) re-inits the rebuilt DOM, so we neither stack
  // duplicate listeners nor skip the re-init.
  if (window.MagniModularBound) return;
  window.MagniModularBound = true;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector('[data-magni-modular]');
    if (section) initModular(section);
  });

  // Design mode: selecting/editing a module block opens the viewer to it.
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:block:select', function (e) {
      var section = e.target.closest && e.target.closest('[data-magni-modular]');
      if (!section || typeof section._magniModularOpen !== 'function') return;
      var slides = section.querySelectorAll('[data-mod-slide]');
      var slide = e.target.closest('[data-mod-slide]');
      var idx = slide ? Array.prototype.indexOf.call(slides, slide) : -1;
      if (idx >= 0) section._magniModularOpen(idx);
    });
  }
})();
